import React, { useEffect, useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

interface BannerState {
  visible: boolean;
  message: string;
}

/**
 * SubscriptionExpiredBanner
 *
 * Listens for the `subscription-expired` custom window event dispatched by the
 * Axios response interceptor (axiosConfig.ts) when the backend returns HTTP 402.
 *
 * Renders a sticky top banner with a warning message and a dismiss button.
 * Automatically hides itself when the user dismisses it.
 */
const SubscriptionExpiredBanner: React.FC = () => {
  const [banner, setBanner] = useState<BannerState>({ visible: false, message: '' });

  useEffect(() => {
    const handleSubscriptionExpired = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setBanner({
        visible: true,
        message: detail?.message || 'Your subscription has expired. Please contact support or renew your plan to regain access.',
      });
    };

    window.addEventListener('subscription-expired', handleSubscriptionExpired);
    return () => window.removeEventListener('subscription-expired', handleSubscriptionExpired);
  }, []);

  if (!banner.visible) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: '#fff3cd',
        borderBottom: '2px solid #ffc107',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
        <FaExclamationTriangle color="#856404" size={18} style={{ flexShrink: 0 }} />
        <span style={{ color: '#856404', fontWeight: 600, fontSize: '14px' }}>
          Subscription Required:&nbsp;
        </span>
        <span style={{ color: '#533f03', fontSize: '14px' }}>{banner.message}</span>
      </div>
      <a
        href="/renew-subscription"
        style={{
          flexShrink: 0,
          fontSize: '13px',
          fontWeight: 700,
          color: '#92400e',
          textDecoration: 'underline',
        }}
      >
        Renew
      </a>
    </div>
  );
};

export default SubscriptionExpiredBanner;
